import { Logger } from '@arshu/common/logger';
import { JsonObject } from '../app/json/jsonValue.js';
import { IView } from './iface/iView.js';
import { IData } from './iface/iData.js';
import { IClientData } from './iface/iClientData.js';
import { HostHandlerKind } from './iface/hostHandlerKind.js';
import { IHostHandlerResolver } from './iface/iHostHandlerResolver.js';
import { IHostActionResolver } from './iface/iHostActionResolver.js';
import { HostResolvedAction, HostResolvedClientData, HostResolvedData } from './hostResolvedHandlers.js';
import { IClient } from './iface/iClient.js';
import { IAction } from './iface/iAction.js';
import { IAppSite } from './iface/iAppSite.js';
import { IRegistry } from './iface/iRegistry.js';
import { IDataReloadable } from './iface/iDataReloadable.js';
import { IDataSource } from './iface/iDataSource.js';
import { ITransientStore } from './iface/iTransientStore.js';
import { GetParentJsonValueHandler } from './iface/getParentJsonValueHandler.js';
import { IViewJson } from './iface/iViewJson.js';
import { AppSiteResult } from './iface/appSiteResult.js';
import { DenySecurityGate, ISecurityGate } from './iface/iSecurityGate.js';
import { TransientCommitConflictException } from './transientCommitConflictException.js';

/**
 * Names a null/undefined argument at the dispatch boundary instead of letting it surface as an
 * unlabelled dereference inside a handler. The polyglot reference uses the BCL's
 * ArgumentNullException.ThrowIfNull; TypeScript's types are erased at runtime, so the same
 * guarantee needs an explicit runtime check. Checks NULL ONLY — "" means absent by convention
 * here and stays legal.
 */
export function throwIfNull(value: unknown, paramName: string): void {
    if (value === null || value === undefined) {
        throw new Error(`Value cannot be null. (Parameter '${paramName}')`);
    }
}

/**
 * Static registry class that holds IView, IData, IAction objects and IRegistry references
 */
export class RegistryStore {
    // #region IView RegistryStore

    static _dynamicViewObjectList: Map<string, IView> = new Map();

    /**
     * Checks if a view provider exists by name
     */
    static containsView(viewTypeName: string): boolean {
        let containsDynamicView = false;
        if (RegistryStore._dynamicViewObjectList.has(viewTypeName.toUpperCase()) === true) {
            containsDynamicView = true;
        }
        return containsDynamicView;
    }

    /**
     * Gets a view provider by name
     */
    static getDynamicView(viewTypeName: string): IView | null {
        let viewObject: IView | null = null;
        const view = RegistryStore._dynamicViewObjectList.get(viewTypeName.toUpperCase());
        if (view !== undefined) {
            viewObject = view;
        }
        return viewObject;
    }

    /**
     * Looks up the registered IView handler for the given type name and invokes its getView.
     * Mirrors invokeDynamicData — every handler family dispatches through one guarded seam in
     * the Registry namespace, so a null threaded in from a caller is named here instead of
     * surfacing as an unlabelled dereference inside a handler. Returns null if no handler is
     * registered for the type.
     */
    static invokeDynamicView(
        viewTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentHtml: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        configJson: JsonObject | null
    ): string | null {
        throwIfNull(viewTypeName, 'viewTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(componentHtml, 'componentHtml');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');

        const viewProvider = RegistryStore._dynamicViewObjectList.get(viewTypeName.toUpperCase());
        if (!viewProvider) {
            return null;
        }
        return viewProvider.getView(appSite, appView, appFile, componentName, componentDepth,
            resolvedAppSite, resolvedComponentName, componentHtml, paramJson, componentJson, configJson);
    }

    /**
     * Looks up the registered IView handler for the given type name and invokes its getViewJson
     * when the handler also implements the JSON-shaped contract. The JSON engines work on HtmlJson
     * directly, so the IView/IViewJson decision belongs to the registry rather than to each loader.
     * Returns null when no handler is registered for the type, or when the registered handler is
     * HTML-only.
     */
    static invokeDynamicViewJson(
        viewTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentHtmlJson: JsonObject,
        paramJson: JsonObject,
        componentJson: JsonObject,
        configJson: JsonObject | null
    ): JsonObject | null {
        throwIfNull(viewTypeName, 'viewTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(componentHtmlJson, 'componentHtmlJson');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');

        const viewProvider = RegistryStore._dynamicViewObjectList.get(viewTypeName.toUpperCase());
        if (!viewProvider) {
            return null;
        }
        const viewJsonProvider = viewProvider as unknown as IViewJson;
        if (typeof viewJsonProvider.getViewJson !== 'function') {
            return null;
        }
        return viewJsonProvider.getViewJson(appSite, appView, appFile, componentName, componentDepth,
            resolvedAppSite, resolvedComponentName, componentHtmlJson, paramJson, componentJson, configJson);
    }

    // #endregion

    // #region IData RegistryStore

    static _dynamicDataObjectList: Map<string, IData> = new Map();

    /**
     * Checks if a data provider exists by name
     */
    static containsData(dataTypeName: string): boolean {
        let containsDynamicData = false;
        if (RegistryStore._dynamicDataObjectList.has(dataTypeName.toUpperCase()) === true) {
            containsDynamicData = true;
        }
        return containsDynamicData || RegistryStore.hostHandles(HostHandlerKind.Data, dataTypeName);
    }

    /**
     * Gets a data handler descriptor by name
     */
    static getDynamicData(dataTypeName: string): IData | null {
        let dataObject: IData | null = null;
        const resolver = RegistryStore._hostHandlerResolver;
        const hostHandles = resolver !== null && resolver.handles(HostHandlerKind.Data, dataTypeName);
        const data = RegistryStore._dynamicDataObjectList.get(dataTypeName.toUpperCase());
        if (RegistryStore._hostHandlersPreferred && hostHandles) {
            dataObject = new HostResolvedData(resolver!, dataTypeName);
        } else if (data !== undefined) {
            dataObject = data;
        } else if (hostHandles) {
            dataObject = new HostResolvedData(resolver!, dataTypeName);
        }
        return dataObject;
    }

    /**
     * Looks up the registered IData provider for the given type name and invokes
     * its sealed getData template method. Centralizes the lookup+invoke pattern
     * shared by every loader's applyDynamicDataHandlers so the dispatch lives in
     * the Registry namespace (one source of truth). Returns null if no provider
     * is registered for the type.
     */
    static invokeDynamicData(
        dataTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): JsonObject | null {
        throwIfNull(dataTypeName, 'dataTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');
        throwIfNull(transientStore, 'transientStore');

        const dataProvider = RegistryStore.getDynamicData(dataTypeName);
        if (!dataProvider) {
            return null;
        }
        return dataProvider.getData(appSite, appView, appFile, componentName, componentDepth,
            resolvedAppSite, resolvedComponentName, paramJson, componentJson,
            transientStore, configJson, getParentJsonValue);
    }

    /**
     * No-op under the LCD stateless dispatch model: handlers are constructed fresh per call and hold
     * no raw-data cache. Raw data lives in the per-request transient store (replay mechanism), not on a
     * handler instance. Retained for API compatibility with existing callers.
     */
    static clearAllDynamicDataRawData(): void {
        // intentionally empty — see PLAN/Architecture/PLAN_Arch_LCD_Dispatch.md
    }

    // #endregion

    // #region IClientData RegistryStore

    static _dynamicClientDataObjectList: Map<string, IClientData> = new Map();

    /**
     * Checks if a client-data preparer exists by name
     */
    static containsClientData(clientDataTypeName: string): boolean {
        return RegistryStore._dynamicClientDataObjectList.has(clientDataTypeName.toUpperCase())
            || RegistryStore.hostHandles(HostHandlerKind.ClientData, clientDataTypeName);
    }

    /**
     * Looks up the registered IClientData preparer for the given type name and invokes its context-light
     * getClientData producer. Mirrors invokeDynamicData — centralizes the lookup+invoke so the SAME
     * producer serves the merge step (Embedded) and, later, the on-demand handler-data path (Remote).
     * Returns null if none is registered for the type.
     */
    static invokeDynamicClientData(
        clientDataTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): JsonObject | null {
        throwIfNull(clientDataTypeName, 'clientDataTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');
        throwIfNull(transientStore, 'transientStore');

        const resolver = RegistryStore._hostHandlerResolver;
        const hostHandles = resolver !== null && resolver.handles(HostHandlerKind.ClientData, clientDataTypeName);
        let clientDataProvider: IClientData | undefined = RegistryStore._dynamicClientDataObjectList.get(clientDataTypeName.toUpperCase());
        if (RegistryStore._hostHandlersPreferred && hostHandles) {
            clientDataProvider = new HostResolvedClientData(resolver!, clientDataTypeName);
        } else if (clientDataProvider === undefined) {
            if (!hostHandles) {
                return null;
            }
            clientDataProvider = new HostResolvedClientData(resolver!, clientDataTypeName);
        }
        return clientDataProvider.getClientData(appSite, appView, appFile, componentName, componentDepth,
            resolvedAppSite, resolvedComponentName, paramJson, componentJson,
            transientStore, configJson, getParentJsonValue);
    }

    // #endregion

    // #region Host handler resolver

    static _hostHandlerResolver: IHostHandlerResolver | null = null;

    /**
     * Subscribes the opting-in host's resolver. The engines raise it for any IData / IClientData Name that has
     * no native registration; with none subscribed every lookup is unchanged. A native registration of the
     * same Name wins unless {@link setHostHandlersPreferred} is on.
     */
    static setHostHandlerResolver(resolver: IHostHandlerResolver | null): void {
        RegistryStore._hostHandlerResolver = resolver;
    }

    private static _hostHandlersPreferred: boolean = false;

    /**
     * When on, a Name the subscribed host resolvers claim is served by the host even if a native handler is
     * registered under it (IData, IClientData and IAction lookups). Names the host does not claim still run
     * natively. Off by default, so every lookup stays native-then-host.
     */
    static setHostHandlersPreferred(preferred: boolean): void {
        RegistryStore._hostHandlersPreferred = preferred;
    }

    private static hostHandles(kind: HostHandlerKind, typeName: string): boolean {
        const resolver = RegistryStore._hostHandlerResolver;
        return resolver !== null && resolver.handles(kind, typeName);
    }

    static _hostActionResolver: IHostActionResolver | null = null;

    /**
     * Subscribes the opting-in host's action resolver. The engines raise it for any IAction Name that has no
     * native registration; with none subscribed every lookup is unchanged. A native registration of the same
     * Name wins unless {@link setHostHandlersPreferred} is on.
     */
    static setHostActionResolver(resolver: IHostActionResolver | null): void {
        RegistryStore._hostActionResolver = resolver;
    }

    // #endregion

    // #region IClient RegistryStore

    static _clientHandlerObjectList: Map<string, IClient> = new Map();

    /**
     * Checks if a client handler exists by name
     */
    static containsClientHandler(clientHandlerTypeName: string): boolean {
        return RegistryStore._clientHandlerObjectList.has(clientHandlerTypeName.toUpperCase());
    }

    /**
     * Looks up the registered IClient for the given type name and invokes its process FILTER over the raw
     * client data (the client "processor" seam — the analogue of the server IAction, but a UNIFIED
     * data-manip+action). Distinct from IClientData (the server "preparer" that produces raw): this consumes
     * that raw. Runs identically in the browser bundle and in the console (--apiclient/--ociclient) simulation,
     * certifying the browser artifact by construction. Returns null if none is registered for the type.
     */
    static invokeDynamicClient(
        clientHandlerTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null,
        getParentJsonValue: GetParentJsonValueHandler | null,
        rawJson: JsonObject
    ): JsonObject | null {
        throwIfNull(clientHandlerTypeName, 'clientHandlerTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');
        throwIfNull(transientStore, 'transientStore');
        throwIfNull(rawJson, 'rawJson');

        const clientHandler = RegistryStore._clientHandlerObjectList.get(clientHandlerTypeName.toUpperCase());
        if (!clientHandler) {
            return null;
        }
        return clientHandler.process(appSite, appView, appFile, componentName, componentDepth,
            resolvedAppSite, resolvedComponentName, paramJson, componentJson,
            transientStore, configJson, getParentJsonValue, rawJson);
    }

    // #endregion

    // #region IAction RegistryStore

    static _dynamicActionObjectList: Map<string, IAction> = new Map();

    /**
     * Checks if an action provider exists by name
     */
    static containsAction(actionTypeName: string): boolean {
        let containsDynamicAction = false;
        if (RegistryStore._dynamicActionObjectList.has(actionTypeName.toUpperCase()) === true) {
            containsDynamicAction = true;
        }
        return containsDynamicAction || (RegistryStore._hostActionResolver?.handlesAction(actionTypeName) ?? false);
    }

    /**
     * Gets an action provider by name
     */
    static getDynamicAction(actionTypeName: string): IAction | null {
        let actionObject: IAction | null = null;
        const resolver = RegistryStore._hostActionResolver;
        const hostHandles = resolver !== null && resolver.handlesAction(actionTypeName);
        const action = RegistryStore._dynamicActionObjectList.get(actionTypeName.toUpperCase());
        if (RegistryStore._hostHandlersPreferred && hostHandles) {
            actionObject = new HostResolvedAction(resolver!, actionTypeName);
        } else if (action !== undefined) {
            actionObject = action;
        } else if (hostHandles) {
            actionObject = new HostResolvedAction(resolver!, actionTypeName);
        }
        return actionObject;
    }

    /**
     * Looks up the registered IAction provider for the given type name and invokes
     * its doAction. Mirrors invokeDynamicData — centralizes lookup+invoke in the
     * Registry namespace.
     */
    static invokeDynamicAction(
        actionTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): void {
        throwIfNull(actionTypeName, 'actionTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');
        throwIfNull(transientStore, 'transientStore');

        const actionProvider = RegistryStore.getDynamicAction(actionTypeName);
        if (!actionProvider) {
            return;
        }
        actionProvider.doAction(appSite, appView, appFile, componentName,
            resolvedAppSite, resolvedComponentName, paramJson, componentJson,
            transientStore, configJson, getParentJsonValue);
    }

    // #endregion

    // #region IAppSite RegistryStore

    static _dynamicAppSiteObjectList: Map<string, IAppSite> = new Map();

    /**
     * Gets the registered dynamic AppSite handler for the given type name.
     */
    static getDynamicAppSite(appSiteTypeName: string): IAppSite | null {
        let appSiteObject: IAppSite | null = null;
        const appSite = RegistryStore._dynamicAppSiteObjectList.get(appSiteTypeName.toUpperCase());
        if (appSite !== undefined) {
            appSiteObject = appSite;
        }
        return appSiteObject;
    }

    /**
     * Looks up the registered IAppSite handler for the given type name and invokes its
     * resolveAppSite. Mirrors invokeDynamicData. This seam is security-bearing: an AppSite handler
     * computes the authoritative auth redirect, so a null argument must be named here rather than
     * reaching the handler as an unlabelled dereference. Returns the resolution result, or null if
     * no handler is registered for the type.
     */
    static invokeDynamicAppSite(
        appSiteTypeName: string,
        appSite: string,
        appView: string,
        paramJson: JsonObject,
        contextJson: JsonObject,
        configJson: JsonObject | null
    ): AppSiteResult | null {
        throwIfNull(appSiteTypeName, 'appSiteTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(contextJson, 'contextJson');

        const appSiteProvider = RegistryStore._dynamicAppSiteObjectList.get(appSiteTypeName.toUpperCase());
        if (!appSiteProvider) {
            return null;
        }
        return appSiteProvider.resolveAppSite(appSite, appView, paramJson, contextJson, configJson);
    }

    // #endregion

    // #region IDataReloadable RegistryStore

    static _appDataReloadables: Map<string, IDataReloadable[]> = new Map();

    /**
     * Reloads every registered app-data-backed runtime service, whichever AppSite key it registered under.
     *
     * There is ONE live App_Data store per run — it is deliberately not AppSite-scoped, and AppSites are
     * just views over it (see AppDataTestUtils.getAppDataDir). So restoring a baseline rewrites files that
     * several differently-keyed managers read, and reloading only the restored AppSite's own key leaves
     * the others serving pre-restore values.
     */
    static reloadAllAppData(): void {
        for (const reloadables of RegistryStore._appDataReloadables.values()) {
            for (const reloadable of reloadables) {
                reloadable.reload();
            }
        }
    }

    // #endregion

    // #region IDataSource RegistryStore

    static _dataSources: Map<string, IDataSource> = new Map();

    /**
     * Returns the registered capture-source resolver for the supplied scheme, or null when none.
     */
    static getDataSource(scheme: string): IDataSource | null {
        if (!scheme || scheme.trim() === '') {
            return null;
        }
        const source = RegistryStore._dataSources.get(scheme.toUpperCase());
        return source !== undefined ? source : null;
    }

    // #endregion

    // #region Register (IRegistry Object)

    /**
     * Registers an IRegistry implementation
     */
    // #region ISecurityGate Slot

    // Endpoint-facing security policy seam. The default rejects OCI machine credentials until a host
    // explicitly registers its concrete security gate (see ISecurityGate).
    private static _securityGate: ISecurityGate = new DenySecurityGate();

    /** The registered endpoint security gate, or the safe default when none is registered. */
    static get securityGate(): ISecurityGate { return RegistryStore._securityGate; }

    /** Registers the concrete endpoint security gate. Called at startup by the security registration. */
    static setSecurityGate(gate: ISecurityGate | null): void {
        if (gate !== null && gate !== undefined) {
            RegistryStore._securityGate = gate;
        }
    }

    // #endregion

    // #region Transient Commit Hook

    // Post-merge working-state commit hook (e.g. SecurityState.commit). Null = no-op, so a host
    // without a security implementation commits nothing. Mirrors the data-handler dispatch model:
    // the engine endpoint invokes the hook by name without referencing the concrete implementation.
    private static _transientCommitHook: ((transientStore: ITransientStore) => void) | null = null;

    /** Registers the post-merge transient commit hook. Called at startup by the security registration. */
    static setTransientCommitHook(hook: (transientStore: ITransientStore) => void): void {
        RegistryStore._transientCommitHook = hook;
    }

    /** Invokes the registered post-merge transient commit hook, if any. No-op when none is registered. */
    static commitTransient(transientStore: ITransientStore): void {
        if (RegistryStore._transientCommitHook !== null) {
            RegistryStore._transientCommitHook(transientStore);
        }
    }

    /**
     * Upper bound on how many times one request may run its merge when the commit keeps losing the
     * compare-and-swap. Every lost attempt means ANOTHER merge committed, so a request racing N-1 other
     * writers loses at most N-1 times and needs at most N attempts: the bound is the largest burst of
     * simultaneous security writers that is guaranteed to get through, not a retry budget for flakiness.
     */
    static readonly MaxCommitAttempts = 32;

    /**
     * Runs a merge together with its commitTransient boundary and, when the commit reports that the store
     * moved on since the merge hydrated, runs the WHOLE merge again.
     *
     * The retry is the semantic mutation re-applied to a fresh snapshot: mergeAndCommit builds a new loader
     * and transient store on every call, so it re-hydrates the store the winner left and re-evaluates every
     * decision against it (a second bootstrap signup then sees the winner's Admin and no longer mints its
     * own). A stale snapshot is never replayed. Any other failure propagates untouched, and the last
     * conflict is rethrown once MaxCommitAttempts is spent.
     */
    static mergeAndCommit<T>(mergeAndCommit: () => T): T {
        for (let attempt = 1; ; attempt++) {
            try {
                return mergeAndCommit();
            } catch (error) {
                if (!(error instanceof TransientCommitConflictException) || attempt >= RegistryStore.MaxCommitAttempts) {
                    throw error;
                }
                Logger.flow(`event=commitConflictRetry attempt=${attempt} maxAttempts=${RegistryStore.MaxCommitAttempts}`, "Registry");
            }
        }
    }

    // #endregion

    static register(register: IRegistry): void {
        if (register !== null && register !== undefined) {
            try {
                const registryType = register.constructor.name.toLowerCase().replace('register', '');
                register.registerView(RegistryStore._dynamicViewObjectList);
                Logger.flow(`event=registerView registryType=${registryType} currentSize=${RegistryStore._dynamicViewObjectList.size}`, "Registry");
                register.registerData(RegistryStore._dynamicDataObjectList);
                Logger.flow(`event=registerData registryType=${registryType} currentSize=${RegistryStore._dynamicDataObjectList.size}`, "Registry");
                register.registerClientData(RegistryStore._dynamicClientDataObjectList);
                Logger.flow(`event=registerClientData registryType=${registryType} currentSize=${RegistryStore._dynamicClientDataObjectList.size}`, "Registry");
                register.registerClient(RegistryStore._clientHandlerObjectList);
                Logger.flow(`event=registerClientHandler registryType=${registryType} currentSize=${RegistryStore._clientHandlerObjectList.size}`, "Registry");
                register.registerAction(RegistryStore._dynamicActionObjectList);
                Logger.flow(`event=registerAction registryType=${registryType} currentSize=${RegistryStore._dynamicActionObjectList.size}`, "Registry");
                register.registerAppSite(RegistryStore._dynamicAppSiteObjectList);
                Logger.flow(`event=registerAppSite registryType=${registryType} currentSize=${RegistryStore._dynamicAppSiteObjectList.size}`, "Registry");
                register.registerReloadable(RegistryStore._appDataReloadables);
                Logger.flow(`event=registerAppData registryType=${registryType} currentSize=${RegistryStore._appDataReloadables.size}`, "Registry");
                register.registerDataSource(RegistryStore._dataSources);
                Logger.flow(`event=registerDataSource registryType=${registryType} currentSize=${RegistryStore._dataSources.size}`, "Registry");
            } catch (ex) {
                Logger.error((ex as Error).message, 'RegistryStore');
            }
        }
    }

    // #endregion
}
